import { AxiosError } from 'axios'

import { useQuery, UseQueryOptions } from '@tanstack/react-query'

import { AreasInterface } from '@/interfaces/ServerInterfaces'

import APIServices from '../../api/APIServices'

export interface AreasQueryParams {
  businessId: number
}

// Create a unique, descriptive key for this query
const AREAS_QUERY_KEY = 'sale-areas'

type AreasResponse = {
  items: AreasInterface[]
  total: number
  per_page: number
  current_page: number
  total_pages: number
}

/**
 * Fetch sale areas from the API
 * Separating API call logic from the hook for better testability
 */
const fetchAreas = async (): Promise<AreasResponse> => {
  const response = await APIServices.get('/administration/area?type=SALE')
  return response.data
}

/**
 * Custom hook for fetching sale areas
 *
 * @param params - Query parameters including businessId
 * @param options - TanStack Query options for customizing query behavior
 * @returns Query result object containing data, loading states, and utility functions
 */
export const useAreasQuery = ({
  params,
  options,
}: {
  params: AreasQueryParams
  options?: Partial<UseQueryOptions<AreasResponse, AxiosError>>
}) => {
  // Query hook
  const queryResult = useQuery<AreasResponse, AxiosError>({
    // Create a structured query key that includes all parameters
    queryKey: [AREAS_QUERY_KEY, params?.businessId],

    // Separate the API call logic from the query configuration
    queryFn: () => fetchAreas(),

    // Default options that can be overridden
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes

    // Apply any custom options
    ...options,
  })

  return {
    areas: queryResult.data?.items || [],
    isLoadingAreas: queryResult.isLoading,
    isErrorAreas: queryResult.isError,
    refetchAreas: queryResult.refetch,
  }
}

/**
 * Helper function to get the areas query key
 * Useful for cache invalidation or prefetching
 */
export const getAreasQueryKey = (params?: AreasQueryParams) => [
  AREAS_QUERY_KEY,
  params?.businessId,
]
